import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Github, ExternalLink } from 'lucide-react';

export const ProjectModal = ({ project, onClose, accent }) => {
  const [repoData, setRepoData] = useState(null);
  const [contributors, setContributors] = useState([]);
  const isLoadingRepo = !repoData;
  const repoCacheKey = `repo-${project.repo}`;
  const contributorsCacheKey = `contributors-${project.repo}`;
  const closeBtnRef = useRef(null);
  const titleId = `project-modal-title-${project.id}`;

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    closeBtnRef.current?.focus();
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocused && previouslyFocused.focus) previouslyFocused.focus();
    };
  }, [onClose]);

  useEffect(() => {
    if (!project.repo) return;

    const cachedRepo = localStorage.getItem(repoCacheKey);
    if (cachedRepo) {
      setRepoData(JSON.parse(cachedRepo));
    } else {
      fetch(`https://api.github.com/repos/${project.repo}`)
        .then(res => res.json())
        .then(data => {
          setRepoData(data);
          localStorage.setItem(repoCacheKey, JSON.stringify(data));
        })
        .catch(() => {});
    }

    const cachedContributors = localStorage.getItem(contributorsCacheKey);
    if (cachedContributors) {
      setContributors(JSON.parse(cachedContributors));
    } else {
      fetch(`https://api.github.com/repos/${project.repo}/contributors`)
        .then(res => res.json())
        .then(data => {
          setContributors(data);
          localStorage.setItem(
            contributorsCacheKey,
            JSON.stringify(data)
          );
        })
        .catch(() => {});
    }

  }, [project.repo, repoCacheKey, contributorsCacheKey]);


  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[999] bg-black/80
                flex items-center justify-center px-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.92, opacity: 0 }}
        transition={{ type: "spring", stiffness: 140, damping: 18 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        className="max-w-xl w-full bg-neutral-900
           p-6 sm:p-10 rounded-3xl relative
           max-h-[85vh] overflow-y-auto"
      >

        <button
          ref={closeBtnRef}
          onClick={onClose}
          aria-label="Close project details"
          className="sticky top-0 ml-auto mb-4 block
                    text-white/40 hover:text-white
                    focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          <span aria-hidden="true">✕</span>
        </button>

        <h2
          id={titleId}
          className="text-3xl font-black italic mb-4"
          style={{ color: accent.color }}
        >
          {project.name}
        </h2>

        <p className="text-white/60 mb-6">
          {project.description || "Project details coming soon."}
        </p>

        {isLoadingRepo ? (
          <div className="flex gap-6 mb-6">
            <div className="w-16 h-4 bg-white/10 rounded animate-pulse" />
            <div className="w-16 h-4 bg-white/10 rounded animate-pulse" />
            <div className="w-16 h-4 bg-white/10 rounded animate-pulse" />
          </div>
        ) : (
          <div className="flex gap-6 text-sm text-white/50 mb-6">
            <span>⭐ {repoData.stargazers_count}</span>
            <span>🍴 {repoData.forks_count}</span>
            <span>👁 {repoData.watchers_count}</span>
          </div>
        )}

        {contributors.length > 0 && (
          <div className="flex items-center gap-2 mb-6">
            {contributors.slice(0, 5).map(c => (
              <img
                key={c.id}
                src={c.avatar_url}
                alt={c.login}
                title={c.login}
                className="w-8 h-8 rounded-full border border-white/20"
              />
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {project.stack.map(s => (
            <span
              key={s}
              className="px-3 py-1 text-xs border border-white/10 rounded"
            >
              {s}
            </span>
          ))}
        </div>

      <div className="flex flex-wrap gap-4 mt-8">
        <a
          href={`https://github.com/${project.repo}`}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-2 px-5 py-2
                    border border-white/20 rounded-lg
                    text-white hover:bg-white hover:text-black
                    transition-all"
        >
          <Github size={16} />
          GitHub
        </a>

        {project.live && (
          <a
            href={project.live}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 px-5 py-2
                      rounded-lg font-black text-black"
            style={{ backgroundColor: accent.color }}
          >
            <ExternalLink size={16} />
            Live Demo
          </a>
        )}
      </div>
      </motion.div>
    </motion.div>
  );
};
